import React, { useCallback, useEffect, useRef } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { getScrollableParent } from '@helpers/getScrollableParent';

interface Params {
  disable?: boolean;
  track?: boolean;
  root?: Element;
  initialTs?: number | null;
}

interface UseTranscriptHook {
  update: (target: number) => void;
  ref: React.RefObject<HTMLDivElement>;
}

const SCROLL_STOP_DEBOUNCE = 500;

const getClosestTimestamp = (timestamps: number[] = [], goal: number) =>
  timestamps.reduce((prev, current) => (Math.abs(current - goal) < Math.abs(prev - goal) ? current : prev), 0);

const buildStyles = (rect: DOMRect, root: Element | undefined): Partial<CSSStyleDeclaration> => {
  if (!root) return {};

  const { top, left, width, height } = rect;
  const { top: rootTop, left: rootLeft } = root.getBoundingClientRect();

  return {
    width: `${width}px`,
    height: `${height + 4}px`,
    left: `${left - rootLeft - 2}px`,
    top: `${top - rootTop - 2}px`,
    zIndex: '1'
  };
};

export const useTranscript = ({ disable, track, root }: Params): UseTranscriptHook => {
  const timestampsRef = useRef<number[]>([]);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const scrollingRef = useRef<boolean>(false);

  const { callback: onStopScroll } = useDebouncedCallback(() => {
    scrollingRef.current = false;
  }, SCROLL_STOP_DEBOUNCE);

  const collect = useCallback(() => {
    if (disable) return;

    requestAnimationFrame(() => {
      tryToGetMarks();
    });
  }, [disable]);

  const tryToGetMarks = () => {
    const collected: number[] = [];
    const marks = document.querySelectorAll('[data-start-time]');

    if (marks) {
      marks.forEach((element) => {
        const startTime = element.getAttribute('data-start-time');
        if (startTime) collected.push(+startTime);
      });

      timestampsRef.current = collected;
    }
  };

  const update = useCallback(
    (target: number) => {
      if (!target || !highlightRef.current) return;

      if (timestampsRef.current.length === 0) {
        tryToGetMarks();
      }

      const closest = getClosestTimestamp(timestampsRef.current, Math.floor(target));
      const mark = document.querySelector(`[data-start-time='${closest}']`);

      if (mark) {
        Object.assign(highlightRef.current.style, buildStyles(mark.getBoundingClientRect(), root));

        if (track && !scrollingRef.current) {
          highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
        }
      }
    },
    [track, timestampsRef.current, root]
  );

  const onScroll = () => {
    scrollingRef.current = true;
    onStopScroll();
  };

  const eventListeners = useCallback(() => {
    const root = getScrollableParent(highlightRef.current);

    root?.addEventListener('scroll', onScroll);

    return () => {
      root?.removeEventListener('scroll', onScroll);
    };
  }, [highlightRef.current]);

  useEffect(collect, [collect, timestampsRef.current]);

  useEffect(eventListeners, [eventListeners]);

  return {
    update,
    ref: highlightRef
  };
};
